// ----------------------------------------------------------------------
// <copyright file="Izenda.DataModel.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Data Model component
 */
import { DEFAULT_SEARCH_PROPERTY, DATA_SOURCE_CHANGES, CHANGED_INDICATOR } from 'logic/data/Izenda.DataModelConstants';
import uiState from 'logic/Izenda.UiState';
import classnames from 'classnames';
import MiddlePanel from 'share/components/layout/Izenda.MiddlePanel';
import ContextMenu from 'share/components/layout/Izenda.ContextMenu';
import QuerySourceType from './QuerySourceType/Izenda.QuerySourceType';
import ExportDataModelPopup from './ExportDataModel/Izenda.ExportDataModelPopup';
import SourceTree from './ExportDataModel/Izenda.SourceTree';
import NotFound from 'share/components/controls/Izenda.NotFound';
import relationship from './Relationship/Izenda.Relationship';
import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import { get, getOr } from 'lodash/fp';

const QUERY_SOURCE_TYPE_STORE_PATH = 'querySourceType';
const RELATIONSHIP_STORE_PATH = 'relationship';
const TABLE_TYPE = 'Table';
const VIEW_TYPE = 'View';
const STORED_PROCEDURE_TYPE = 'Stored Procedure';
const FUNCTION_TYPE = 'Function';

const viewComponents = {
  tables: {
    component: QuerySourceType,
    storePath: QUERY_SOURCE_TYPE_STORE_PATH,
    props: {
      querySourceType: TABLE_TYPE
    }
  },
  views: {
    component: QuerySourceType,
    storePath: QUERY_SOURCE_TYPE_STORE_PATH,
    props: {
      querySourceType: VIEW_TYPE
    }
  },
  storeprocedures: {
    component: QuerySourceType,
    storePath: QUERY_SOURCE_TYPE_STORE_PATH,
    props: {
      querySourceType: STORED_PROCEDURE_TYPE
    }
  },
  functions: {
    component: QuerySourceType,
    storePath: QUERY_SOURCE_TYPE_STORE_PATH,
    props: {
      querySourceType: FUNCTION_TYPE
    }
  },
  relationship: {
    component: relationship,
    storePath: RELATIONSHIP_STORE_PATH
  },

  notFound: {
    component: NotFound
  }
};

function getComponentInfo(key) {
  return getOr(viewComponents.notFound, key, viewComponents);
}

@connect()
export default class DataModel extends Component {
  static propTypes: {
    store: PropTypes.object.isRequired,
    onSaveCallback: PropTypes.func
  };

  /**
   * Handle when componet did mount
   */
  componentDidMount() {
    this.props.store.init();
  }

  /**
   * handle when componentWillunmount
   */
  componentWillUnmount() {
    this.props.store.destroy();
  }

  render() {
    const store = this.props.store;
    const mainContent = <div className="izenda-TableLayout-Cell">{this.renderRightContent()}</div>;
    const middelPanel = this.props.store.indicator && (
      <MiddlePanel>
        <ContextMenu
          dataSource={this.props.store.dataModelMenu}
          activeKey={this.props.store.activeLink}
          enabled={true}
          onSelected={this.changeItem}
          renderChangedIcon={this.renderChangedIcon}
          className="izenda-DataModel-Middle"
        />
      </MiddlePanel>
    );

    return (
      <div className="izenda-TableLayout">
        {middelPanel}
        {mainContent}
      </div>
    );
  }

  /**
   * Handle change selected item
   */
  changeItem = newLink => {
    this.props
      .confirmDirtyForm(uiState.isDirty, {
        onConfirmDirtyForm: uiState.onOkDirtyForm
      })
      .then(() => {
        this.props.store.changeSelectItem(newLink);
        // Reset search text
        const info = getComponentInfo(newLink);
        const store = get(info.storePath, this.props.store);
        const pageInfo = store.pageInfo;
        // Only set search value to any page that has search box
        pageInfo && pageInfo.setSearchValues && pageInfo.setSearchValues(DEFAULT_SEARCH_PROPERTY, '');
        return null;
      });
  };

  /**
   * Render right content when click middle panel
   */
  renderRightContent() {
    const { onSaveCallback } = this.props;
    const info = getComponentInfo(this.props.store.activeLink);
    const store = get(info.storePath, this.props.store);
    return store
      ? <info.component {...info.props}
                        store={store}
                        parentStore={this.props.store}
                        onSaveCallback={onSaveCallback}
                        onExport={this.onExport}/>
      : <info.component />;
  }

  /**
   * Render changed icon
   */
  renderChangedIcon = ({ backendType }) => {
    const isChange = this.props.store.loadItemIndicator(backendType) === CHANGED_INDICATOR;
    const className = classnames({
      'icon-attention warning-color': isChange
    });

    const title = isChange && DATA_SOURCE_CHANGES;

    return <span className={className} title={title} />;
  };

  /**
   * On Export Data Model
   */
  onExport = () => {
    const {props, props : { store, loader } } = this;

    loader.startLoading();
    store.loadSourceQuerySourceTree()
    .then((tree) => {
      props.openPopup(ExportDataModelPopup, {
        title: props.localization.getText('DATA_MODEL_LABEL_EXPORT'),
        treeComponent: SourceTree,
        tree: tree,
        store: store
      });
    })
    .finally(loader.endLoading.bind(loader));
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/Izenda.DataModel.jsx